import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useMediaQuery from '@mui/material/useMediaQuery';

import MobileBridgeComponent from 'src/assets/images/mobileBridgeComponent.png';
import ConnectWalletPopover from 'src/components/ConnectWalletPopover';
import DisclaimerPopover from 'src/components/DisclaimerPopover';
import Footer from 'src/components/Footer';
import GetDYDXPopover from 'src/components/GetDYDXAddressPopover';
import Header from 'src/components/Header';
import BlockClickPopover from 'src/components/Mobile/BlockClickPopover';
import MobileHeader from 'src/components/Mobile/Header';
import TabButton from 'src/components/TabButton';
import { device } from 'src/constants/muiSize';
import { DYDX_TOKEN_ADDRESS } from 'src/constants/tokens';
import { Wallets } from 'src/constants/wallets';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useAllowanceSWR } from 'src/hooks/useAllowanceSWR';
import { useBalanceSWR } from 'src/hooks/useBalanceSWR';
import { useCurrentTab } from 'src/hooks/useCurrentTab';
import { useCurrentWallet } from 'src/hooks/useCurrentWallet';
import useProvider from 'src/hooks/useProivder';
import { useTokenBalanceSWR } from 'src/hooks/useTokenBalanceSWR';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { ExtensionDefault } from 'src/recoils/extension';
import type { WalletInfo } from 'src/types/wallet';
import { connectWallet, isWalletInstalled } from 'src/utils/extension';

import Bridge from './components/Bridge';
import Governance from './components/Governance';
import MediaColumn from './components/MediaColumn';
import Send from './components/Send';
import Staking from './components/Staking';
import { Background, ContentsContainer, MediaColumnContainer, MobileBackground, MobileComponent, MobileScaffold, Scaffold, TabButtonContainer } from './styled';

export default function Home() {
  const { enqueueSnackbar } = useSnackbar();

  const windowSize = useWindowSize();

  const [currentExtensionNetworkId, setCurrentExtensionNetworkId] = useState<string>();

  const { currentWallet, setCurrentWallet } = useCurrentWallet();
  const { currentAccount, setCurrentAccount } = useCurrentAccount();
  const { currentTab } = useCurrentTab();

  const [isOpenPopover, setIsOpenPopover] = useState(false);

  const [isOpenConnectWalletPopover, setIsOpenConnectWalletPopover] = useState(false);
  const [isOpenGetDYDXPopover, setIsOpenGetDYDXPopover] = useState(false);
  const [isOpenDisclaimerPopover, setIsOpenDisclaimerPopover] = useState(true);

  const { rawProvider } = useProvider();

  const halfWindowSize = useMemo(() => windowSize.width / 2, [windowSize.width]);

  const halfWindowHeight = useMemo(() => windowSize.height / 2, [windowSize.height]);

  const isLaptop = useMediaQuery(device.minLaptop);

  const walletList = useMemo(
    () =>
      Wallets.map((item) => {
        const isInstalled = isWalletInstalled(item.walletType);

        return {
          ...item,
          isInstalled,
        };
      }),
    [],
  );
  const allowanceData = useAllowanceSWR({ accAddress: currentAccount.accAddress ? currentAccount.accAddress : undefined });

  const tokenBalance = useTokenBalanceSWR(currentWallet.name ? { address: currentAccount.accAddress, tokenContractAddress: DYDX_TOKEN_ADDRESS } : undefined);

  const ethereumBalance = useBalanceSWR({ address: currentAccount.accAddress });

  const cosmosBalance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const handleEthereumWallet = useCallback(
    async (wallet: WalletInfo) => {
      try {
        const response = await connectWallet(wallet.walletType);

        if (response.responseChainId) {
          setCurrentExtensionNetworkId(response.responseChainId);
        }

        if (response.response[0]) {
          setCurrentAccount({
            ...currentAccount,
            accAddress: response.response[0],
          });
        }

        setIsOpenGetDYDXPopover(true);
      } catch (e) {
        enqueueSnackbar('Fail to connect wallet', { variant: 'error', autoHideDuration: 3000 });
      } finally {
        setIsOpenConnectWalletPopover(false);
      }
    },
    [currentAccount, enqueueSnackbar, setCurrentAccount],
  );

  useEffect(() => {
    rawProvider?.on('accountsChanged', (accounts) => {
      const ethereumAccount = accounts as string[];

      if (ethereumAccount[0] !== currentAccount.accAddress) {
        setCurrentWallet(ExtensionDefault);
        setIsOpenGetDYDXPopover(false);

        setCurrentAccount({
          accAddress: '',
          mnemonic: '',
          cosmosAddress: '',
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, rawProvider, setIsOpenGetDYDXPopover]);

  useEffect(() => {
    try {
      rawProvider?.on('chainChanged', (chainId) => {
        const id = chainId as string;
        if (currentExtensionNetworkId !== id) {
          setCurrentExtensionNetworkId(id);
        }

        void ethereumBalance.mutate();
        void tokenBalance.mutate();
        void allowanceData.mutate();
        void cosmosBalance.mutate();
      });
    } catch (error) {
      setCurrentExtensionNetworkId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExtensionNetworkId, rawProvider]);

  useEffect(() => {
    if (!currentWallet.name) {
      setCurrentWallet(ExtensionDefault);
      setCurrentAccount({
        accAddress: '',
        mnemonic: '',
        cosmosAddress: '',
      });
    }
  }, [currentWallet.name, currentWallet.walletType, setCurrentAccount, setCurrentWallet]);

  useEffect(() => {
    if (currentWallet.name) {
      if (!tokenBalance.data) {
        void tokenBalance.mutate();
      }
      if (!ethereumBalance.data) {
        void ethereumBalance.mutate();
      }
      if (!allowanceData.data) {
        void allowanceData.mutate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWallet.name]);

  return (
    <div>
      {isLaptop ? (
        <>
          <Background>
            <Header
              onClickConnectWallet={(a) => {
                setIsOpenConnectWalletPopover(a);
              }}
              onClickDisconnect={() => {
                setCurrentWallet(ExtensionDefault);
                setCurrentAccount({
                  ...currentAccount,

                  accAddress: '',
                });
              }}
            />
            <ContentsContainer>
              <Scaffold>
                {currentTab.tabIndex === 0 ? (
                  <Bridge currentExtensionNetworkId={currentExtensionNetworkId || ''} />
                ) : currentTab.tabIndex === 1 ? (
                  <Staking />
                ) : currentTab.tabIndex === 2 ? (
                  <Send />
                ) : (
                  <Governance />
                )}

                <ConnectWalletPopover
                  marginThreshold={0}
                  walletList={walletList.filter((item) => item.walletType === 'Metamask')}
                  handleAccount={handleEthereumWallet}
                  anchorReference="anchorPosition"
                  anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
                  open={isOpenConnectWalletPopover}
                  onClose={() => setIsOpenConnectWalletPopover(false)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                />

                <GetDYDXPopover
                  marginThreshold={0}
                  wallet={walletList[0]}
                  currentExtensionNetworkId={currentExtensionNetworkId}
                  anchorReference="anchorPosition"
                  anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
                  open={isOpenGetDYDXPopover}
                  onClose={() => setIsOpenGetDYDXPopover(false)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                />

                <DisclaimerPopover
                  marginThreshold={0}
                  anchorReference="anchorPosition"
                  anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
                  open={isOpenDisclaimerPopover}
                  onClose={() => setIsOpenDisclaimerPopover(false)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                />
              </Scaffold>
              <MediaColumnContainer>
                <MediaColumn />
              </MediaColumnContainer>
            </ContentsContainer>
          </Background>
          <Footer />
        </>
      ) : (
        <>
          <MobileBackground>
            <MobileHeader
              onClick={() => {
                setIsOpenPopover(true);
              }}
            />

            <TabButtonContainer>
              <TabButton
                text="BRIDGE"
                isActive
                isMobile
                onClick={() => {
                  setIsOpenPopover(true);
                }}
              />
              <TabButton
                text="STAKING"
                isMobile
                onClick={() => {
                  setIsOpenPopover(true);
                }}
              />
              <TabButton
                text="TRANSFER"
                isMobile
                onClick={() => {
                  setIsOpenPopover(true);
                }}
              />
              <TabButton
                text="GOVERNANCE"
                isMobile
                onClick={() => {
                  setIsOpenPopover(true);
                }}
              />
            </TabButtonContainer>
            <MobileScaffold
              onClick={() => {
                setIsOpenPopover(true);
              }}
            >
              <MobileComponent src={MobileBridgeComponent} />
            </MobileScaffold>

            <BlockClickPopover
              marginThreshold={0}
              anchorReference="anchorPosition"
              anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
              open={isOpenPopover}
              onClose={() => setIsOpenPopover(false)}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'center',
              }}
            />
          </MobileBackground>
          <Footer />
        </>
      )}
    </div>
  );
}
